import type { Note, PracticeRecord, ReviewLog, StudySession, Todo } from '@/domain/types'
import { formatLocalDate } from '@/lib/date'

function localDate(ms: number) {
  return formatLocalDate(new Date(ms))
}

function tagsOf(item: Pick<Note, 'tags'>) {
  return Array.isArray(item.tags) ? item.tags : []
}

function isMistake(item: Note) {
  return item.category === '错题记录' || item.type === 'mistake' || tagsOf(item).includes('错题记录')
}

function periodBounds(date: string, period: string) {
  const selected = new Date(`${date}T12:00:00`)
  if (period === 'day') return { start: date, end: date }
  if (period === 'week') {
    const start = new Date(selected)
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7))
    const end = new Date(start)
    end.setDate(end.getDate() + 6)
    return { start: formatLocalDate(start), end: formatLocalDate(end) }
  }
  if (period === 'month') {
    return {
      start: formatLocalDate(new Date(selected.getFullYear(), selected.getMonth(), 1, 12)),
      end: formatLocalDate(new Date(selected.getFullYear(), selected.getMonth() + 1, 0, 12)),
    }
  }
  return { start: '0000-01-01', end: '9999-12-31' }
}

export function buildPeriodStats(
  date: string,
  period: string,
  allSessions: StudySession[],
  allTodos: Todo[],
  allNotes: Note[],
  allPractices: PracticeRecord[],
  allReviewLogs: ReviewLog[] = [],
) {
  const { start, end } = periodBounds(date, period)
  const inRange = (ms: number) => {
    const value = localDate(ms)
    return value >= start && value <= end
  }
  const sessions = allSessions.filter(item => !item.deletedAtMs && item.status === 'completed' && inRange(item.startedAtMs))
  const todos = allTodos.filter(item => !item.deletedAtMs && (period === 'day' ? item.taskDate === date : item.taskDate >= start && item.taskDate <= end))
  const notes = allNotes.filter(item => !item.deletedAtMs && inRange(item.createdAtMs))
  const practices = allPractices.filter(item => !item.deletedAtMs && inRange(item.createdAtMs))
  const focusMinutes = sessions.reduce((sum, item) => sum + Math.round(item.durationSeconds / 60), 0)
  const moduleStats: Record<string, { count: number; minutes: number }> = {}
  const hourlyDist: Record<string, { count: number; minutes: number }> = {}

  for (const session of sessions) {
    const moduleName = session.focusModule || '综合'
    const minutes = Math.round(session.durationSeconds / 60)
    moduleStats[moduleName] ||= { count: 0, minutes: 0 }
    moduleStats[moduleName].count += 1
    moduleStats[moduleName].minutes += minutes
    const hour = new Date(session.startedAtMs).getHours()
    const slot = hour < 6 ? '凌晨' : hour < 9 ? '清晨' : hour < 12 ? '上午' : hour < 14 ? '午间' : hour < 18 ? '下午' : hour < 22 ? '晚间' : '深夜'
    hourlyDist[slot] ||= { count: 0, minutes: 0 }
    hourlyDist[slot].count += 1
    hourlyDist[slot].minutes += minutes
  }

  const focusTimeSeries: Array<{ key: string; label: string; minutes: number }> = []
  let focusTimeGrain: 'day' | 'month' = 'day'
  if (period === 'week' || period === 'month') {
    const byDate = new Map<string, number>()
    for (const session of sessions) {
      const key = localDate(session.startedAtMs)
      byDate.set(key, (byDate.get(key) || 0) + Math.round(session.durationSeconds / 60))
    }
    const cursor = new Date(`${start}T12:00:00`)
    const last = new Date(`${end}T12:00:00`)
    while (cursor <= last) {
      const key = formatLocalDate(cursor)
      focusTimeSeries.push({
        key,
        label: period === 'week'
          ? ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][cursor.getDay()]
          : `${cursor.getDate()}日`,
        minutes: byDate.get(key) || 0,
      })
      cursor.setDate(cursor.getDate() + 1)
    }
  } else if (period === 'all' && sessions.length > 0) {
    focusTimeGrain = 'month'
    const byMonth = new Map<string, number>()
    for (const session of sessions) {
      const key = localDate(session.startedAtMs).slice(0, 7)
      byMonth.set(key, (byMonth.get(key) || 0) + Math.round(session.durationSeconds / 60))
    }
    const keys = [...byMonth.keys()].sort()
    const [startYear, startMonth] = keys[0].split('-').map(Number)
    const [endYear, endMonth] = keys[keys.length - 1].split('-').map(Number)
    const cursor = new Date(startYear, startMonth - 1, 1, 12)
    const last = new Date(endYear, endMonth - 1, 1, 12)
    while (cursor <= last) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`
      focusTimeSeries.push({ key, label: `${cursor.getMonth() + 1}月`, minutes: byMonth.get(key) || 0 })
      cursor.setMonth(cursor.getMonth() + 1)
    }
  }

  return {
    date,
    period,
    focusMinutes,
    focusCount: sessions.length,
    completedTodos: todos.filter(item => item.completed).length,
    totalTodos: todos.length,
    mistakesCount: notes.filter(isMistake).length,
    pendingMistakes: allNotes.filter(item => !item.deletedAtMs && isMistake(item)).length,
    moduleStats,
    hourlyDist,
    focusTimeSeries,
    focusTimeGrain,
    recentInspirations: allNotes.filter(item => !item.deletedAtMs).sort((a, b) => b.createdAtMs - a.createdAtMs).slice(0, 5),
    practiceCount: practices.length,
    inspirationCount: notes.length,
    // 当日复习闪念数：按复习时间（reviewedAt）落在周期范围内统计
    reviewedCount: allReviewLogs.filter(item => !item.deletedAtMs && inRange(new Date(item.reviewedAt).getTime())).length,
  }
}

export function buildComprehensiveStats(sessions: StudySession[], practices: PracticeRecord[], notes: Note[] = []) {
  const completedSessions = sessions.filter(item => !item.deletedAtMs && item.status === 'completed')
  const dates = [...new Set(completedSessions.map(item => localDate(item.startedAtMs)))].sort().reverse()
  let consecutiveDays = 0
  const cursor = new Date(`${formatLocalDate()}T12:00:00`)
  for (const date of dates) {
    if (date !== formatLocalDate(cursor)) break
    consecutiveDays += 1
    cursor.setDate(cursor.getDate() - 1)
  }

  const activePractices = practices.filter(item => !item.deletedAtMs)
  const byModule = new Map<string, { module: string; correct: number; total: number; recordCount: number }>()
  const byTrend = new Map<string, { module: string; date: string; correct: number; total: number; duration: number }>()
  for (const record of activePractices) {
    const aggregate = byModule.get(record.module) || { module: record.module, correct: 0, total: 0, recordCount: 0 }
    aggregate.correct += record.correctCount
    aggregate.total += record.totalCount
    aggregate.recordCount += 1
    byModule.set(record.module, aggregate)
    const date = localDate(record.createdAtMs)
    const key = `${record.module}|${date}`
    const trend = byTrend.get(key) || { module: record.module, date, correct: 0, total: 0, duration: 0 }
    trend.correct += record.correctCount
    trend.total += record.totalCount
    trend.duration += record.durationMinutes
    byTrend.set(key, trend)
  }
  return {
    consecutiveDays,
    practiceByModule: [...byModule.values()],
    practiceTrend: [...byTrend.values()].sort((a, b) => a.date.localeCompare(b.date)),
    totalQuestions: activePractices.reduce((sum, item) => sum + item.totalCount, 0),
    recentPractices: activePractices.sort((a, b) => b.createdAtMs - a.createdAtMs).slice(0, 10).map(toLegacyPractice),
    totalPractices: activePractices.length,
    // 今日待复习闪卡数：与复习模块口径一致——到期（含从未复习的 null/未开始）且
    // 排除错题记录与每日精读卡（这两类不进入复习队列）
    pendingReviewCount: notes.filter(item => {
      if (item.deletedAtMs) return false
      if (isMistake(item)) return false
      if (tagsOf(item).includes('每日精读')) return false
      if (!item.reviewState || item.reviewState.state === 'new') return true
      const dueAtMs = item.reviewState.dueAt ? new Date(item.reviewState.dueAt).getTime() : null
      return dueAtMs === null || Number.isNaN(dueAtMs) || (item.nextReviewAtMs ?? 0) <= Date.now() || dueAtMs <= Date.now()
    }).length,
  }
}

export function toLegacySession(session: StudySession) {
  return {
    id: session.id,
    subject: session.subject,
    focus_module: session.focusModule,
    minutes: Math.round(session.durationSeconds / 60),
    reached_target: session.reachedTarget ? 1 : 0,
    created_at: new Date(session.startedAtMs).toISOString(),
    status: session.status,
    device_id: session.deviceId,
  }
}

export function toLegacyPractice(record: PracticeRecord) {
  return {
    id: record.id,
    module: record.module,
    correct_count: record.correctCount,
    total_count: record.totalCount,
    duration_minutes: record.durationMinutes,
    note: record.note,
    source: record.source || 'manual',
    created_at: new Date(record.createdAtMs).toISOString(),
  }
}
